import {useState, useMemo, useDeferredValue, useCallback} from 'react'
import PropTypes from 'prop-types'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import {AddCircleRounded} from '@mui/icons-material'
import {
  StyledTableWrapper,
  StyledSearchAddButtonBox,
  StyledSearchTextField,
  StyledAddButton,
  StyledLoadingBox,
  StyledTablePagination,
  StyledSearchIcon,
  StyledTableContainer,
  StyledBodyTableRow,
  StyledHeaderTableCell,
  StyledBodyTableCell
} from './ReusableTableStyles'

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25]

const ReusableTable = ({
  data,
  columns,
  searchKeys,
  tableContainerSx,
  paperProps,
  getRowKey,
  onRowClick,
  onAddClick,
  searchable = false,
  searchPlaceholder = 'Search...',
  paginated = false,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  tableAriaLabel = 'data table',
  stickyHeader = false,
  variant = 'default',
  type = 'default',
  emptyMessage = 'No data available',
  loading = false,
  showAddButton = false,
  addButtonText = 'Add',
  addButtonDisabled = false,
  addButtonIcon = <AddCircleRounded />
}) => {
  const [searchInput, setSearchInput] = useState('')
  const deferredSearch = useDeferredValue(searchInput)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0])

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value)
    setPage(0)
  }, [])

  const filteredData = useMemo(() => {
    if (!searchable || !deferredSearch) return data
    const lowerSearch = deferredSearch.toLowerCase()
    const keys = searchKeys || columns.filter((col) => !col.disableSearch).map((col) => col.field)
    return data.filter((row) =>
      keys.some((key) => {
        const value = typeof key === 'function' ? key(row) : row[key]
        return value != null && String(value).toLowerCase().includes(lowerSearch)
      })
    )
  }, [data, deferredSearch, searchable, searchKeys, columns])

  const displayedData = useMemo(() => {
    if (!paginated) return filteredData
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [filteredData, paginated, page, rowsPerPage])

  const handleChangePage = useCallback((_, newPage) => {
    setPage(newPage)
  }, [])

  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(Number.parseInt(e.target.value, 10))
    setPage(0)
  }, [])

  const renderTableBody = useMemo(() => {
    const safeColSpan = Math.max(1, columns.length)
    const resolveRowKey = (row, index) => {
      if (getRowKey) return getRowKey(row, index)
      return index
    }
    if (loading) {
      return (
        <TableRow>
          <StyledBodyTableCell colSpan={safeColSpan} align='center' tableVariant={variant} tableType={type}>
            <StyledLoadingBox>
              <CircularProgress size={40} />
            </StyledLoadingBox>
          </StyledBodyTableCell>
        </TableRow>
      )
    }
    if (displayedData.length > 0) {
      return displayedData.map((row, index) => (
        <StyledBodyTableRow
          key={resolveRowKey(row, index)}
          rowStyles={typeof row._rowSx === 'object' || typeof row._rowSx === 'function' ? row._rowSx : undefined}
          isInteractive={!!onRowClick}
          onClick={onRowClick ? (event) => onRowClick(event, row, index) : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          onKeyDown={
            onRowClick
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onRowClick(event, row, index)
                  }
                }
              : undefined
          }
        >
          {columns.map((col) => (
            <StyledBodyTableCell
              key={col.field || col.headerName}
              align={col.align || 'left'}
              cellStyles={col.cellSx}
              tableVariant={variant}
              tableType={type}
            >
              {col.renderCell ? col.renderCell(row, index) : row[col.field]}
            </StyledBodyTableCell>
          ))}
        </StyledBodyTableRow>
      ))
    }
    return (
      <TableRow>
        <StyledBodyTableCell colSpan={safeColSpan} align='center' tableVariant={variant} tableType={type}>
          <Typography variant='body2'>{emptyMessage}</Typography>
        </StyledBodyTableCell>
      </TableRow>
    )
  }, [loading, displayedData, columns, emptyMessage, getRowKey, onRowClick, variant, type])

  return (
    <StyledTableWrapper>
      {(searchable || showAddButton) && (
        <StyledSearchAddButtonBox>
          {searchable && (
            <StyledSearchTextField
              size='small'
              placeholder={searchPlaceholder}
              onChange={handleSearchChange}
              value={searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <StyledSearchIcon />
                  </InputAdornment>
                )
              }}
            />
          )}
          {showAddButton && (
            <StyledAddButton
              variant='outlined'
              startIcon={addButtonIcon}
              onClick={onAddClick}
              disabled={addButtonDisabled}
            >
              {addButtonText}
            </StyledAddButton>
          )}
        </StyledSearchAddButtonBox>
      )}
      <StyledTableContainer
        component={Paper}
        tableContainerStyles={tableContainerSx}
        tableVariant={variant}
        {...paperProps}
      >
        <Table aria-label={tableAriaLabel} stickyHeader={stickyHeader}>
          {columns.some((col) => col.headerName) && (
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <StyledHeaderTableCell
                    key={col.field || col.headerName}
                    align={col.align || 'left'}
                    headerStyles={col.headerSx}
                    tableVariant={variant}
                    tableType={type}
                  >
                    {col.headerName || ''}
                  </StyledHeaderTableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>{renderTableBody}</TableBody>
        </Table>
      </StyledTableContainer>
      {paginated && filteredData.length > 0 && (
        <StyledTablePagination
          component='div'
          tableVariant={variant}
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          showFirstButton
          showLastButton
        />
      )}
    </StyledTableWrapper>
  )
}

ReusableTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      headerName: PropTypes.string,
      align: PropTypes.oneOf(['left', 'right', 'center', 'inherit', 'justify']),
      headerSx: PropTypes.object,
      cellSx: PropTypes.object,
      renderCell: PropTypes.func,
      disableSearch: PropTypes.bool
    })
  ).isRequired,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func])),
  paginated: PropTypes.bool,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  tableAriaLabel: PropTypes.string,
  stickyHeader: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'ghost']),
  type: PropTypes.oneOf(['compact', 'default', 'relaxed']),
  emptyMessage: PropTypes.string,
  tableContainerSx: PropTypes.object,
  paperProps: PropTypes.object,
  getRowKey: PropTypes.func,
  onRowClick: PropTypes.func,
  loading: PropTypes.bool,
  showAddButton: PropTypes.bool,
  addButtonText: PropTypes.string,
  onAddClick: PropTypes.func,
  addButtonDisabled: PropTypes.bool,
  addButtonIcon: PropTypes.node
}

export default ReusableTable
